import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MainLayout from '../../layouts/MainLayout';
import userStore from '../../store/userStore';
import api, { getErrorMessage } from '../../services/api';
import PartnerCard from './components/PartnerCard';
import NewUserForm from './components/NewUserForm';
import UnassignedJudicial from './components/UnassignedJudicial';

const TABS = [
  { id: 'partners', label: 'Despachos' },
  { id: 'unassigned', label: 'Judiciales sin despacho' },
];

/**
 * Despachos colaboradores (solo admin; docs/partners-plan.md). Aquí se dan de
 * alta los despachos y sus abogados, se decide qué clientes ve cada uno y se
 * asigna despacho a los judiciales que no lo tienen.
 */
const PartnersPage = () => {
  const user = userStore(state => state.user);
  const [activeTab, setActiveTab] = useState('partners');
  const [partners, setPartners] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [adding, setAdding] = useState(false);

  const loadPartners = useCallback(
    () =>
      api
        .get('/partners')
        .then(({ data }) => setPartners(data.data || []))
        .catch(err => toast.error(getErrorMessage(err))),
    []
  );

  useEffect(() => {
    if (user.role !== 'admin') return;
    loadPartners();
    api
      .get('/users/list', { params: { role: 'company' } })
      .then(({ data }) =>
        setCompanies(
          (data || [])
            .map(c => ({ ...c, _id: String(c._id) }))
            .sort((a, b) => (a.username || a.usermail || '').localeCompare(b.username || b.usermail || ''))
        )
      )
      .catch(err => toast.error(getErrorMessage(err)));
  }, [user.role, loadPartners]);

  if (user.role !== 'admin') {
    return <Navigate to='/dashboard' replace />;
  }

  const createPartner = async body => {
    try {
      await api.post('/partners', body);
      toast.success('Despacho creado');
      setAdding(false);
      loadPartners();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <MainLayout>
      <div className='bg-white shadow-lg rounded-lg border border-slate-200'>
        <header className='px-5 py-4 border-b border-slate-100'>
          <h1 className='text-2xl font-semibold text-slate-900'>Despachos colaboradores</h1>
          <p className='text-sm text-slate-500 mt-1'>
            Ven solo los expedientes judiciales que tengan asignados, de los clientes que se marquen aquí.
          </p>
        </header>

        <div className='border-b border-slate-200'>
          <nav className='flex space-x-6 px-5'>
            {TABS.map(tab => (
              <button
                key={tab.id}
                type='button'
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors outline-none focus:outline-none ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className='p-5 space-y-4'>
          {activeTab === 'partners' && (
            <>
              {adding ? (
                <NewUserForm
                  title='Nuevo despacho'
                  companies={companies}
                  onSubmit={createPartner}
                  onCancel={() => setAdding(false)}
                />
              ) : (
                <div className='flex justify-end'>
                  <button
                    type='button'
                    onClick={() => setAdding(true)}
                    className='px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700'
                  >
                    + Nuevo despacho
                  </button>
                </div>
              )}
              {partners.length === 0 ? (
                <p className='text-sm text-slate-500'>Todavía no hay despachos.</p>
              ) : (
                <ul className='space-y-3'>
                  {partners.map(p => (
                    <PartnerCard key={p._id} partner={p} companies={companies} onChanged={loadPartners} />
                  ))}
                </ul>
              )}
            </>
          )}
          {activeTab === 'unassigned' && <UnassignedJudicial partners={partners} />}
        </div>
      </div>
    </MainLayout>
  );
};

export default PartnersPage;
